// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';

const ownProfile = (user: ProfileUser, viewer: Viewer) => viewer.isAuth && viewer.id === user.id;

type Props = {|
  subject: 'boards' | 'domainPins' | 'pins' | 'topics' | 'users' | 'search',
  surface: 'community' | 'following' | 'followers' | 'profile' | 'shop',
  user: ProfileUser,
  viewer: Viewer,
|};

export class ProfileEmptyStateText extends React.PureComponent<Props> {
  following() {
    const { user, viewer } = this.props;
    return ownProfile(user, viewer) ? this.ownFollowingText() : this.othersFollowingText();
  }

  followers() {
    const { user, viewer } = this.props;
    return ownProfile(user, viewer)
      ? i18n._(
          'Nobody has followed your boards yet',
          'Text displayed when the current user is viewing their own profile and has yet to have their boards followed',
        )
      : interpolateNamedTemplate(
          i18n._(
            "Nobody has followed {{ firstName }}'s boards yet",
            'Text displayed when a no one has followed a user’s boards',
          ),
          {
            firstName: user.first_name,
          },
        );
  }

  community() {
    const { user, viewer } = this.props;
    return ownProfile(user, viewer)
      ? i18n._(
          'No one is following you',
          'Text displayed when the current user is viewing their own profile and has yet to have their profile/boards followed',
        )
      : interpolateNamedTemplate(
          i18n._(
            'Be the first to follow {{ firstName }}',
            'Text displayed when a no one has followed a user’s profile/boards',
          ),
          {
            firstName: user.first_name,
          },
        );
  }

  othersFollowingText() {
    const {
      subject,
      user: { first_name: firstName },
    } = this.props;

    switch (subject) {
      case 'boards':
        return interpolateNamedTemplate(
          i18n._(
            "{{ firstName }} hasn't followed any boards yet",
            'Text displayed when a user has not followed any boards',
          ),
          { firstName },
        );
      case 'topics':
        return interpolateNamedTemplate(
          i18n._(
            "{{ firstName }} hasn't picked any topics to follow yet",
            'Text displayed when a user has not followed any topics',
          ),
          { firstName },
        );
      case 'users':
        return interpolateNamedTemplate(
          i18n._(
            "{{ firstName }} hasn't followed anyone yet",
            'Text displayed when a user has not followed anyone',
          ),
          { firstName },
        );
      default:
        return null;
    }
  }

  ownFollowingText() {
    const { subject } = this.props;

    switch (subject) {
      case 'boards':
        return i18n._(
          'You haven’t followed any boards yet',
          'Text displayed when the current user is viewing their own profile and has yet to follow any boards',
        );
      case 'topics':
        return i18n._(
          'You haven’t picked any topics to follow yet',
          'Text displayed when the current user is viewing their own profile and has yet to follow any topics',
        );
      case 'users':
        return i18n._(
          'You haven’t followed anyone yet',
          'Text displayed when the current user is viewing their own profile and has yet to follow any other users',
        );
      default:
        return null;
    }
  }

  profile() {
    const { subject, user, viewer } = this.props;

    if (ownProfile(user, viewer)) {
      // we do not show these messages on own profile
      return null;
    }

    const { first_name: firstName } = user;

    switch (subject) {
      case 'pins':
        return interpolateNamedTemplate(
          i18n._(
            "{{ firstName }} hasn't saved any Pins yet",
            'Text displayed when a user has not saved any pins',
          ),
          {
            firstName,
          },
        );
      case 'boards':
        return interpolateNamedTemplate(
          i18n._(
            "{{ firstName }} hasn't created any boards yet",
            'Text displayed when a user has not created any boards',
          ),
          {
            firstName,
          },
        );
      case 'domainPins':
        return interpolateNamedTemplate(
          i18n._(
            "{{ firstName }} hasn't had any pins saved from their domain yet",
            'Text displayed when a user has not had any images from their website pinned',
          ),
          { firstName },
        );
      default:
        return null;
    }
  }

  shop() {
    const { subject } = this.props;

    switch (subject) {
      case 'search':
        return interpolateNamedTemplate(
          i18n._(
            "Sorry, we couldn't find any Pins for this search.",
            'Text displayed when a search result is empty',
          ),
        );
      default:
        return null;
    }
  }

  render() {
    const { surface } = this.props;

    switch (surface) {
      case 'followers':
        return this.followers();
      case 'following':
        return this.following();
      case 'profile':
        return this.profile();
      case 'community':
        return this.community();
      case 'shop':
        return this.shop();
      default:
        return null;
    }
  }
}

export default withViewer(ProfileEmptyStateText);
